import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { collaboratorAllocationsQueryKeys } from './useCollaboratorAllocationsQuery';
import { AllocationDurationTypes, AllocationStatuses, AllocationTypes } from '@/features/Collaborators/Models';
import type { CollaboratorAllocationFormValues } from '@/features/Collaborators/Allocations/components/CollaboratorAllocationForm/CollaboratorAllocationForm';

export interface CreateCollaboratorAllocationVariables {
  collaboratorId: string;
  values: CollaboratorAllocationFormValues;
}

function sanitizeBucket(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value ?? 0);

  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }

  return numeric;
}

export function useCreateCollaboratorAllocationMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ collaboratorId, values }: CreateCollaboratorAllocationVariables) => {
      if (!collaboratorId) {
        throw new Error('Colaborador não informado.');
      }

      if (!user) {
        throw new Error('Usuário não autenticado.');
      }

      if (!values.startDate) {
        throw new Error('Data de início é obrigatória.');
      }

      const startDate = Timestamp.fromDate(values.startDate);
      const endDate = values.endDate ? Timestamp.fromDate(values.endDate) : null;

      if (values.durationType === AllocationDurationTypes.Defined && !endDate) {
        throw new Error('Data de fim é obrigatória quando a duração é definida.');
      }

      const now = Timestamp.now();
      const payload = {
        allocationType: values.allocationType,
        durationType: values.durationType,
        bucketType: values.bucketType,
        bucket: sanitizeBucket(values.bucket),
        startDate,
        endDate,
        clientId: values.clientId ?? null,
        clientName: values.clientName ?? null,
        clientLogo: values.clientLogo ?? null,
        clientResponsible: values.clientResponsible ?? null,
        departmentId: values.departmentId ?? null,
        departmentName: values.departmentName ?? null,
        projectId: values.projectId ?? null,
        projectName: values.projectName ?? null,
        capacityId: values.capacityId ?? null,
        capacityLabel: values.capacityLabel ?? null,
        capacityTypeId: values.capacityTypeId ?? null,
        capacityTypeName: values.capacityTypeName ?? null,
        capacityTechnologyId: values.capacityTechnologyId ?? null,
        capacityTechnologyName: values.capacityTechnologyName ?? null,
        capacitySeniorityId: values.capacitySeniorityId ?? null,
        capacitySeniorityName: values.capacitySeniorityName ?? null,
        rate: values.rate ?? null,
        noticeReason: values.allocationType === AllocationTypes.Product ? values.noticeReason?.trim() || null : null,
        responsibleId: values.responsibleId ?? null,
        responsibleName: values.responsibleName ?? null,
        responsibleAvatar: values.responsibleAvatar ?? null,
        createdById: user.uid,
        createdByName: user.displayName ?? user.email ?? 'Usuário Raro',
        createdByAvatar: user.photoURL ?? null,
        updatedById: user.uid,
        updatedByName: user.displayName ?? user.email ?? 'Usuário Raro',
        updatedByAvatar: user.photoURL ?? null,
        status: AllocationStatuses.Active,
        cancelReason: null,
        terminationAppliedAt: null,
        canceledAt: null,
        canceledById: null,
        canceledByName: null,
        canceledByAvatar: null,
        createdAt: now,
        updatedAt: now,
      };

      const allocationsRef = collection(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations'
      );

      const docRef = await addDoc(allocationsRef, payload);
      return docRef.id;
    },
    onSuccess: (_, variables) => {
      const queryKey = collaboratorAllocationsQueryKeys.list(variables.collaboratorId);
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export type UseCreateCollaboratorAllocationMutationResult = ReturnType<
  typeof useCreateCollaboratorAllocationMutation
>;
